import { useCallback } from 'react';
import { LOCAL_FUNCTIONS } from '@/integrations/supabase/growthHubClient';
import { getStoredUtmParams } from '@/components/providers/TrackingProvider';
import {
  getFbc,
  getFbp,
  generateEventId,
  getTrackingIdentifiers,
} from '@/lib/firstPartyCookies';
import {
  trackContactFormSubmit,
  trackEmailClick,
  trackEvent as trackGaEvent,
  trackPhoneClick,
  trackQuoteFormSubmit,
  trackRoofingCtaClick,
} from '@/lib/analytics';
import {
  trackGoogleLeadConversion,
  trackGoogleQuoteConversion,
  trackGoogleEstimateConversion,
  setGoogleEnhancedConversionUserData,
  trackLeadMagnetSubmit,
} from '@/lib/googleTracking';
import { getGoogleClickIds } from '@/lib/marketingAttribution';


// Get visitor and session IDs
const getVisitorId = () => localStorage.getItem('obc_visitor_id') || '';
const getSessionId = () => sessionStorage.getItem('obc_session_id') || '';

interface TrackingData {
  session_id: string;
  visitor_id: string;
  event_type: string;
  event_name: string;
  page_url: string;
  page_path: string;
  page_title: string;
  [key: string]: unknown;
}

const isValidEmail = (value?: string | null) =>
  !!value && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

const hasUsablePhone = (value?: string | null) =>
  !!value && value.replace(/\D/g, '').length >= 7;

const hasMeaningfulAddress = (value?: string | null) =>
  !!value && value.trim().length >= 5;

const hasContactData = (leadData: {
  email?: string;
  phone?: string;
  address?: string;
}) =>
  isValidEmail(leadData.email) ||
  hasUsablePhone(leadData.phone) ||
  hasMeaningfulAddress(leadData.address);

const sendTrackingEvent = async (data: TrackingData) => {
  try {
    const res = await fetch(LOCAL_FUNCTIONS.visitorTracking, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        action: 'track_event',
        data,
      }),
    });
    // If Growth Hub is down, silently absorb — tracking must never break the app
    if (!res.ok) {
      console.debug('Tracking endpoint returned', res.status);
    }
  } catch {
    // Network failure — silently ignore
  }
};

export const useVisitorTracking = () => {
  // Track page view
  const trackPageView = useCallback((pagePath: string, pageTitle: string) => {
    // Meta Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'PageView');
    }

    // OBC Tracker - use trackEvent if available
    if (typeof window !== 'undefined' && (window as any).obcTracker) {
      const tracker = (window as any).obcTracker;
      if (typeof tracker.trackEvent === 'function') {
        tracker.trackEvent('page_view', { page_path: pagePath, page_title: pageTitle });
      }
    }

    // Growth Hub
    sendTrackingEvent({
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      event_type: 'page_view',
      event_name: 'page_view',
      page_url: window.location.href,
      page_path: pagePath,
      page_title: pageTitle,
    });
  }, []);

  // Track CTA click
  const trackCTAClick = useCallback((ctaName: string, ctaLocation: string = 'unknown') => {
    // Meta Pixel custom event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', 'CTAClick', {
        cta_name: ctaName,
        cta_location: ctaLocation,
      });
    }

    // OBC Tracker
    if (typeof window !== 'undefined' && (window as any).obcTracker) {
      const tracker = (window as any).obcTracker;
      if (typeof tracker.trackEvent === 'function') {
        tracker.trackEvent('cta_click', { cta_name: ctaName, cta_location: ctaLocation });
      }
    }

    // Growth Hub
    sendTrackingEvent({
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      event_type: 'cta_click',
      event_name: 'cta_click',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      cta_name: ctaName,
      cta_location: ctaLocation,
    });
  }, []);

  // Track scroll depth
  const trackScrollDepth = useCallback((depth: number) => {
    // OBC Tracker
    if (typeof window !== 'undefined' && (window as any).obcTracker) {
      const tracker = (window as any).obcTracker;
      if (typeof tracker.trackEvent === 'function') {
        tracker.trackEvent('scroll_depth', { depth });
      }
    }

    // Growth Hub
    sendTrackingEvent({
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      event_type: 'scroll_depth',
      event_name: 'scroll_depth',
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      scroll_depth: depth,
    });
  }, []);

  // Lead value map for ROAS optimization
  const LEAD_VALUES: Record<string, number> = {
    'roof_replacement': 15000, 'metal_roofing': 25000, 'tile_roofing': 20000,
    'shingle_roofing': 12000, 'roof_repair': 3000, 'emergency': 5000,
    'inspection': 500, 'lead-magnet': 0, 'general': 8000,
  };

  // Track form submission with conversion - ROUTES THROUGH SERVER-SIDE TRACKING
  const trackFormSubmission = useCallback(async (
    formName: string, 
    leadData: {
      name?: string;
      email?: string;
      phone?: string;
      address?: string;
      source_page?: string;
      service_type?: string;
      city?: string;
    }
  ) => {
    // Do not fire any lead/submission tracking without usable contact data.
    // Growth Hub treats several downstream `form_submission` analytics events
    // as website submissions, so this gate must happen before Meta, GA4, OBC
    // tracker, and server-side CAPI calls.
    if (!hasContactData(leadData)) {
      console.debug('Skipping form submission tracking: no usable contact data', { formName });
      return;
    }

    // Generate unique event ID for deduplication between client + server
    const eventId = generateEventId();
    const identifiers = getTrackingIdentifiers();
    const utmParams = getStoredUtmParams();

    const leadType = leadData.service_type || 'general';
    const leadValue = LEAD_VALUES[leadType] ?? LEAD_VALUES['general'];

    // Client-side Meta Pixel Lead event WITH event_id for deduplication
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Lead', {
        content_name: formName,
        content_category: leadType,
        value: leadValue,
        currency: 'USD',
      }, { eventID: eventId }); // Pass event_id for dedup
    }

    // Lightweight GA4/GTM submit signal (no Ads conversion fires here).
    if (formName.includes('quote')) {
      trackQuoteFormSubmit(formName, window.location.pathname);
    } else {
      trackContactFormSubmit(formName, window.location.pathname);
    }

    // Canonical Google conversion fire — routes to the right Ads conversion
    // label based on form kind, and always emits GA4 `generate_lead`.
    // `event_id` is shared with Meta CAPI (above) for deduplication.
    try {
      const clickIds = getGoogleClickIds();
      const basePayload = {
        event_id: eventId,
        value: leadValue,
        currency: 'USD',
        form_id: formName,
        form_name: formName,
        source_page: leadData.source_page || window.location.pathname,
        service_type: leadType,
        city: leadData.city,
        page_path: window.location.pathname,
        page_location: window.location.href,
        visitor_id: identifiers.visitorId,
        session_id: identifiers.sessionId,
        ...clickIds,
      };

      // Classify: lead-magnet / TOFU forms never fire the primary Ads lead.
      const isLeadMagnet =
        formName === 'tofu_lead_magnet' ||
        formName.startsWith('lead_magnet') ||
        formName.includes('newsletter') ||
        formName.includes('download') ||
        leadType === 'lead-magnet';

      if (isLeadMagnet) {
        trackLeadMagnetSubmit({
          event_id: eventId,
          form_name: formName,
          source_page: basePayload.source_page,
          city: leadData.city,
        });
      } else if (formName.includes('quote')) {
        trackGoogleQuoteConversion(basePayload);
      } else if (formName === 'quick_estimate') {
        trackGoogleEstimateConversion(basePayload);
      } else {
        // callbacks, emergency, leak detection, contact, callback_form, etc.
        trackGoogleLeadConversion(basePayload);
      }

      // Enhanced Conversions (hashed user_data) — guarded by env + ad_user_data consent.
      void setGoogleEnhancedConversionUserData({
        email: leadData.email,
        phone: leadData.phone,
        firstName: leadData.name?.split(' ')[0],
        lastName: leadData.name?.split(' ').slice(1).join(' '),
        city: leadData.city,
        country: 'US',
      });
    } catch {
      // never let tracking break a form
    }


    // OBC Tracker
    if (typeof window !== 'undefined' && (window as any).obcTracker) {
      const tracker = (window as any).obcTracker;
      if (typeof tracker.trackEvent === 'function') {
        tracker.trackEvent('form_submission', { form_name: formName, ...leadData });
      }
    }

    // Read optional test event code from URL for Facebook Test Events tool
    const testEventCode = new URLSearchParams(window.location.search).get('fbTestEventCode');

    try {
      await fetch(LOCAL_FUNCTIONS.serverSideTracking, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          event_name: 'Lead',
          event_id: eventId,
          event_source_url: window.location.href,
          name: leadData.name || null,
          email: leadData.email || null,
          phone: leadData.phone || null,
          address: leadData.address || null,
          city: leadData.city || null,
          // Hint to Growth Hub: this is the CAPI mirror of an already-recorded
          // lead, not a standalone website submission. Hub should record it
          // for Meta CAPI dedup but EXCLUDE it from "All Website Submissions".
          submission_type: 'capi_mirror',
          is_capi_mirror: true,
          ...(testEventCode ? { test_event_code: testEventCode } : {}),
          user_data: {
            email: leadData.email,
            phone: leadData.phone,
            first_name: leadData.name?.split(' ')[0],
            last_name: leadData.name?.split(' ').slice(1).join(' '),
            address: leadData.address,
            city: leadData.city,
            external_id: identifiers.visitorId,
            client_user_agent: navigator.userAgent,
            fbc: identifiers.fbc || undefined,
            fbp: identifiers.fbp || undefined,
          },
          custom_data: {
            content_name: formName,
            content_category: leadType,
            source_page: leadData.source_page,
            form_name: formName,
            value: leadValue,
            currency: 'USD',
            lead_type: leadType,
            submission_type: 'capi_mirror',
            is_capi_mirror: true,
          },
          utm_source: utmParams.utm_source,
          utm_medium: utmParams.utm_medium,
          utm_campaign: utmParams.utm_campaign,
          utm_term: utmParams.utm_term,
          utm_content: utmParams.utm_content,
        }),
      });
    } catch (error) {
      console.debug('Server-side tracking error:', error);
    }
  }, []);

  // Track custom event
  const trackEvent = useCallback((eventName: string, eventData: Record<string, unknown> = {}) => {
    trackGaEvent(eventName, eventData as Record<string, string | number | boolean | undefined>);
    // Meta Pixel custom event
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('trackCustom', eventName, eventData);
    }

    // OBC Tracker
    if (typeof window !== 'undefined' && (window as any).obcTracker) {
      const tracker = (window as any).obcTracker;
      if (typeof tracker.trackEvent === 'function') {
        tracker.trackEvent(eventName, eventData);
      }
    }

    // Growth Hub
    sendTrackingEvent({
      session_id: getSessionId(),
      visitor_id: getVisitorId(),
      event_type: eventName,
      event_name: eventName,
      page_url: window.location.href,
      page_path: window.location.pathname,
      page_title: document.title,
      ...eventData,
    });
  }, []);

  return {
    trackPageView,
    trackCTAClick,
    trackScrollDepth,
    trackFormSubmission,
    trackEvent,
    trackPhoneClick,
    trackEmailClick,
  };
};

export default useVisitorTracking;
